import React from 'react';
import type { ReportData } from '../tokens/types';
import { DataTable, Box, Stack, Row, T, Divider } from '../primitives/Universal';
import { fmtPct, truncate } from './formatUtils';

const WEDGE_COLORS = ['#6021B3', '#8B5CF6', '#B794F4', '#D6BCFA', '#E9D5FF', '#F3E8FF'];

/**
 * Body of the Shareholding pattern — table + top-holders legend, no Section wrapper
 * and no header text. Reused by the merged Structure & Relations module so the
 * existing UI is preserved verbatim. Wrappers (standalone module / S&R) own the
 * heading + "As of …" line.
 */
export const ShareholdingBlock: React.FC<{ data: ReportData }> = ({ data }) => {
    const s = data.shareholding;
    if (!s || !s.rows.length) return null;

    const sorted = [...s.rows].sort((a, b) => b.percent - a.percent);
    const top = sorted.slice(0, 5);
    const total = s.rows.reduce((acc, r) => acc + r.percent, 0);
    // The Shareholder column gets ~58% of body width (the rest is split between
    // Type at 28% and % stake at 14%). At our 8.5pt body size that's ~50 chars
    // before the cell hits the next column; long fund / vehicle names like
    // "MULTIPLES PRIVATE EQUITY FUND IV - INDIA INVESTMENT VEHICLE" used to spill
    // into the Type column and shove the row baseline. Truncating the name + the
    // already-snug type label keeps every row on a single line.
    const tableRows = sorted.slice(0, 12).map((r) => ({
        name: truncate(r.holderName, 48),
        type: truncate(r.holderType, 30),
        pct: fmtPct(r.percent, 2),
    }));

    return (
        <>
            <Row gap={16} align="stretch">
                <Box flex={1}>
                    <DataTable
                        columns={[
                            { key: 'name', header: 'Shareholder' },
                            // Type column was 18% — too narrow for values like "Managing
                            // Director" or "Independent Director" which wrapped onto two
                            // lines and visually drifted the row baselines. 28% absorbs
                            // every realistic holderType without wrap.
                            { key: 'type', header: 'Type', width: '28%' },
                            { key: 'pct', header: '% stake', width: '14%', align: 'right' },
                        ]}
                        rows={tableRows}
                    />
                </Box>

                <Box width={170}>
                    <T size="micro" tone="muted" transform="uppercase" letterSpacing={0.8} marginBottom={6}>
                        Top holders
                    </T>

                    <Row height={10} borderRadius={5} overflow="hidden">
                        {top.map((r, i) => (
                            <Box
                                key={`bar-${r.holderName}-${r.holderType}-${i}`}
                                flex={r.percent}
                                background={WEDGE_COLORS[i % WEDGE_COLORS.length]}
                            />
                        ))}
                    </Row>

                    <Stack marginTop={8} gap={3}>
                        {top.map((r, i) => (
                            <Row key={`legend-${r.holderName}-${r.holderType}-${i}`} align="center" gap={6}>
                                <Box width={8} height={8} background={WEDGE_COLORS[i % WEDGE_COLORS.length]} borderRadius={2} />
                                <T size="micro" tone="muted" flex={1}>
                                    {r.holderName}
                                </T>
                                <T size="micro" family="bold">
                                    {fmtPct(r.percent, 1)}
                                </T>
                            </Row>
                        ))}
                    </Stack>

                    <Divider marginVertical={8} />

                    <T size="micro" tone="mutedSoft">
                        Total reported · {fmtPct(total, 2)}
                    </T>
                </Box>
            </Row>
        </>
    );
};

// NOTE: the standalone `shareholdingModule` was removed — its content is now
// rendered inline inside `RelatedParties` (Structure & Relations module). Only
// the `ShareholdingBlock` body export remains and is consumed there.
